import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone } from 'lucide-react';
import { Button } from './ui/button';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { path: '/', label: 'Home' },
    { path: '/about', label: 'About' },
    { path: '/therapy-services', label: 'Therapy Services' },
    { path: '/sports-performance', label: 'Sports & Performance' },
    { path: '/fees', label: 'Fees' },
    { path: '/scheduling', label: 'Scheduling' },
    { path: '/contact', label: 'Contact' },
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white/95 backdrop-blur-sm shadow-md' : 'bg-white'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center py-4">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3 group">
            <img
              src="https://customer-assets.emergentagent.com/job_wellness-confidence/artifacts/8uneor3p_5B1FDA40-334A-4855-84E5-C226E88B7797.png"
              alt="Mind Games Logo"
              className="h-16 w-auto transition-transform duration-300 group-hover:scale-105"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-8 xl:gap-10">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`text-sm font-medium transition-colors duration-200 relative group whitespace-nowrap ${
                  location.pathname === link.path
                    ? 'text-sage-700'
                    : 'text-gray-700 hover:text-sage-600'
                }`}
              >
                {link.label}
                <span
                  className={`absolute -bottom-1 left-0 h-0.5 bg-sage-600 transition-all duration-200 ${
                    location.pathname === link.path ? 'w-full' : 'w-0 group-hover:w-full'
                  }`}
                />
              </Link>
            ))}
          </nav>

          {/* CTA Button */}
          <div className="hidden lg:flex items-center space-x-5 xl:space-x-6">
            <a href="tel:9512680161" className="flex items-center text-gray-700 hover:text-sage-600 transition-colors whitespace-nowrap">
              <Phone className="h-4 w-4 mr-2" />
              <span className="text-sm font-medium">(951) 268-0161</span>
            </a>
            <Link to="/scheduling">
              <Button className="bg-sage-600 hover:bg-sage-700 text-white whitespace-nowrap">
                Book Consultation
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
          >
            {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden py-4 border-t border-gray-200">
            <nav className="flex flex-col space-y-4">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`text-base font-medium transition-colors ${
                    location.pathname === link.path
                      ? 'text-teal-600'
                      : 'text-gray-700 hover:text-teal-600'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              <a
                href="tel:9512680161"
                className="flex items-center text-gray-700 hover:text-sage-600 transition-colors pt-2 border-t border-gray-200"
              >
                <Phone className="h-4 w-4 mr-2" />
                <span className="text-base font-medium">(951) 268-0161</span>
              </a>
              <Link to="/scheduling" onClick={() => setIsMobileMenuOpen(false)}>
                <Button className="w-full bg-sage-600 hover:bg-sage-700 text-white">
                  Book Consultation
                </Button>
              </Link>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
